import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { adminApi } from '../../lib/adminApi';
import { useToast } from '../../hooks/use-toast';
import { HeartHandshake, Sparkles, Phone, Mail, Trash2, CheckCircle2, RefreshCw, Inbox } from 'lucide-react';

const TYPE_META = {
  counselling: { label: 'Counselling', Icon: HeartHandshake, accent: 'bg-blue-100 text-blue-800' },
  volunteer:   { label: 'Volunteer',   Icon: Sparkles,       accent: 'bg-amber-100 text-amber-800' },
};

const STATUS_META = {
  new:       { label: 'New',       cls: 'bg-emerald-100 text-emerald-800' },
  contacted: { label: 'Contacted', cls: 'bg-blue-100 text-blue-800' },
  closed:    { label: 'Closed',    cls: 'bg-gray-100 text-gray-600' },
};

const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

const EnquiriesManager = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');    // '', 'counselling', 'volunteer'
  const [statusFilter, setStatusFilter] = useState(''); // '', 'new', 'contacted', 'closed'
  const [counts, setCounts] = useState({ counselling: { new: 0 }, volunteer: { new: 0 } });
  const { toast } = useToast();

  const load = async () => {
    setLoading(true);
    try {
      const [listRes, countRes] = await Promise.all([
        adminApi.get('/enquiries', { params: { type: typeFilter || undefined, status: statusFilter || undefined } }),
        adminApi.get('/enquiries/counts'),
      ]);
      setItems(Array.isArray(listRes.data) ? listRes.data : []);
      setCounts(countRes.data || counts);
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [typeFilter, statusFilter]);

  const setStatus = async (id, status) => {
    try {
      await adminApi.patch(`/enquiries/${id}`, { status });
      toast({ title: 'Updated', description: `Marked as ${status}` });
      load();
    } catch (e) {
      toast({ title: 'Error', description: 'Update failed', variant: 'destructive' });
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this enquiry permanently?')) return;
    try {
      await adminApi.delete(`/enquiries/${id}`);
      toast({ title: 'Deleted' });
      load();
    } catch (e) {
      toast({ title: 'Error', description: 'Delete failed', variant: 'destructive' });
    }
  };

  const totalNew = (counts.counselling?.new || 0) + (counts.volunteer?.new || 0);

  return (
    <div className="space-y-6" data-testid="enquiries-manager">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Inbox className="w-6 h-6 text-[#1565C0]" />
          <h2 className="text-2xl font-bold">Enquiries — Counselling &amp; Volunteers</h2>
          {totalNew > 0 && (
            <span className="ml-2 inline-flex items-center rounded-full bg-red-500 text-white text-xs font-semibold px-2 py-0.5" data-testid="enquiries-new-count">
              {totalNew} new
            </span>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* Filter row */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-gray-500 mr-1">Type:</span>
        {['', 'counselling', 'volunteer'].map((t) => (
          <button
            key={t || 'all'}
            onClick={() => setTypeFilter(t)}
            className={`px-3 py-1 rounded-full text-xs font-medium border ${
              typeFilter === t ? 'bg-[#1565C0] border-[#1565C0] text-white' : 'border-gray-300 text-gray-700 hover:border-[#1565C0]'
            }`}
            data-testid={`filter-type-${t || 'all'}`}
          >
            {t ? TYPE_META[t].label : 'All'}
            {t && counts[t]?.new ? <span className="ml-1 opacity-70">· {counts[t].new}</span> : null}
          </button>
        ))}
        <span className="text-xs text-gray-500 ml-3 mr-1">Status:</span>
        {['', 'new', 'contacted', 'closed'].map((s) => (
          <button
            key={s || 'any'}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1 rounded-full text-xs font-medium border ${
              statusFilter === s ? 'bg-emerald-600 border-emerald-600 text-white' : 'border-gray-300 text-gray-700 hover:border-emerald-600'
            }`}
            data-testid={`filter-status-${s || 'any'}`}
          >
            {s ? STATUS_META[s].label : 'Any'}
          </button>
        ))}
      </div>

      {loading && items.length === 0 ? (
        <div className="text-center py-12 text-gray-500 text-sm">Loading…</div>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-gray-500 text-sm">
            No enquiries {statusFilter ? `in "${STATUS_META[statusFilter].label}"` : 'yet'}.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {items.map((it) => {
            const meta = TYPE_META[it.type] || TYPE_META.counselling;
            const status = STATUS_META[it.status || 'new'];
            const { Icon } = meta;
            return (
              <Card key={it.id} data-testid={`enquiry-row-${it.id}`}>
                <CardHeader className="pb-2 flex flex-row items-center justify-between gap-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${meta.accent}`}>
                      <Icon className="w-3 h-3" /> {meta.label}
                    </span>
                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${status.cls}`}>
                      {status.label}
                    </span>
                    <CardTitle className="text-base">{it.name}</CardTitle>
                    <span className="text-xs text-gray-400">· {fmt(it.created_at)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {it.status !== 'contacted' && (
                      <Button size="sm" variant="outline" onClick={() => setStatus(it.id, 'contacted')} data-testid={`mark-contacted-${it.id}`}>
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Contacted
                      </Button>
                    )}
                    {it.status !== 'closed' && (
                      <Button size="sm" variant="ghost" onClick={() => setStatus(it.id, 'closed')} data-testid={`mark-closed-${it.id}`}>
                        Close
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => remove(it.id)} className="text-red-600 hover:text-red-700" data-testid={`delete-${it.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="text-sm text-gray-700 space-y-2">
                  <div className="flex flex-wrap gap-x-4 gap-y-1">
                    {it.phone && (
                      <a href={`https://wa.me/${it.phone.replace(/\D/g, '')}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#1565C0] hover:underline">
                        <Phone className="w-3.5 h-3.5" /> {it.phone}
                      </a>
                    )}
                    {it.email && (
                      <a href={`mailto:${it.email}`} className="inline-flex items-center gap-1 text-[#1565C0] hover:underline">
                        <Mail className="w-3.5 h-3.5" /> {it.email}
                      </a>
                    )}
                  </div>
                  {it.type === 'counselling' && (
                    <div className="text-xs text-gray-600">
                      {it.concern && <div><b>Concern:</b> {it.concern}</div>}
                      {(it.preferred_date || it.preferred_time) && (
                        <div><b>Preferred:</b> {it.preferred_date} {it.preferred_time}</div>
                      )}
                    </div>
                  )}
                  {it.type === 'volunteer' && (
                    <div className="text-xs text-gray-600">
                      {Array.isArray(it.interests) && it.interests.length > 0 && (
                        <div><b>Interests:</b> {it.interests.join(', ')}</div>
                      )}
                      {it.availability && <div><b>Availability:</b> {it.availability}</div>}
                      {it.age_group && <div><b>Age:</b> {it.age_group}</div>}
                    </div>
                  )}
                  {it.message && (
                    <p className="text-gray-800 whitespace-pre-line border-l-2 border-gray-200 pl-3">{it.message}</p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default EnquiriesManager;
